import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ShoppingCart,
  Eye,
  Clock,
  CheckCircle2,
  Truck,
  Wallet,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import {
  KPICard,
  SearchInput,
  ExportButton,
  StatusBadge,
  BulkActionBar,
} from "@/components/admin/shared";
import { formatFCFA } from "@cieda/utils";
import { ordersApi } from "@/lib/admin-api/orders";
import type { OrderStatus, OrderSummary } from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  confirmed: "Confirmée",
  processing: "En préparation",
  shipped: "Expédiée",
  delivered: "Livrée",
  cancelled: "Annulée",
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  wave: "Wave",
  orange_money: "Orange Money",
  cash: "Espèces",
  cash_on_delivery: "Livraison contre remb.",
  virement: "Virement",
};

const STATUS_OPTIONS: readonly OrderStatus[] = [
  "pending", "confirmed", "processing", "shipped", "delivered", "cancelled",
];

const PAYMENT_METHOD_OPTIONS: readonly string[] = [
  "wave", "orange_money", "cash", "cash_on_delivery", "virement",
];

const PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// Export columns (matches OrderSummary shape)
// ---------------------------------------------------------------------------

const EXPORT_COLUMNS = [
  { key: "reference", label: "Référence" },
  { key: "customer_name", label: "Client" },
  { key: "customer_phone", label: "Téléphone" },
  { key: "created_at", label: "Date" },
  { key: "total_ttc", label: "Total (FCFA)" },
  { key: "payment_method", label: "Méthode paiement" },
  { key: "status", label: "Statut" },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function OrderManagement() {
  useAdminPageTitle("Commandes");
  const navigate = useNavigate();

  // Server-side filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "">("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [page, setPage] = useState(1);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  // ---------------------------------------------------------------------------
  // Main orders list — server-side filtered + paginated
  // ---------------------------------------------------------------------------

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "orders", "list", page, statusFilter, paymentMethodFilter, search],
    queryFn: () =>
      ordersApi.list({
        page,
        limit: PAGE_SIZE,
        status: statusFilter || null,
        payment_method: paymentMethodFilter || null,
        q: search || null,
      }),
    placeholderData: (prev) => prev, // keep stale data while fetching next page
  });

  const orders: readonly OrderSummary[] = data?.orders ?? [];
  const totalOrders = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalOrders / PAGE_SIZE));

  // ---------------------------------------------------------------------------
  // KPI stats — parallel count-only requests (limit=1 for cheap HEAD-like calls)
  // ---------------------------------------------------------------------------

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const { data: kpis } = useQuery({
    queryKey: ["admin", "orders", "kpis"],
    queryFn: async () => {
      const [allOrders, pendingOrders, confirmedOrders, processingOrders, shippedOrders, deliveredOrders, todayOrders] =
        await Promise.all([
          ordersApi.list({ limit: 1 }),
          ordersApi.list({ limit: 1, status: "pending" }),
          ordersApi.list({ limit: 1, status: "confirmed" }),
          ordersApi.list({ limit: 1, status: "processing" }),
          ordersApi.list({ limit: 1, status: "shipped" }),
          ordersApi.list({ limit: 1, status: "delivered" }),
          ordersApi.list({ limit: 200, date_from: todayStart.toISOString() }),
        ]);
      const todayRevenue = todayOrders.orders
        .filter((o) => ["confirmed", "processing", "shipped", "delivered"].includes(o.status))
        .reduce((sum, o) => sum + (o.total_ttc ?? 0), 0);
      return {
        total: allOrders.pagination.total,
        pending: pendingOrders.pagination.total + confirmedOrders.pagination.total,
        inProgress: processingOrders.pagination.total + shippedOrders.pagination.total,
        delivered: deliveredOrders.pagination.total,
        todayRevenue,
      };
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // ---------------------------------------------------------------------------
  // Filter reset helper
  // ---------------------------------------------------------------------------

  const resetPage = useCallback(() => setPage(1), []);

  // ---------------------------------------------------------------------------
  // Selection handlers
  // ---------------------------------------------------------------------------

  const toggleSelectAll = useCallback(() => {
    const pageIds = orders.map((o) => o.id);
    const allSelected = pageIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of pageIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of pageIds) next.add(id);
        return next;
      });
    }
  }, [orders, selectedIds]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // ---------------------------------------------------------------------------
  // Bulk actions (trigger server mutations when backend bulk route is ready)
  // ---------------------------------------------------------------------------

  const handleBulkStatusUpdate = useCallback(() => {
    toast.success(`${selectedIds.size} commande(s) mise(s) à jour`);
    clearSelection();
  }, [selectedIds, clearSelection]);

  const handleBulkAssignDelivery = useCallback(() => {
    toast.success(`Livraison assignée pour ${selectedIds.size} commande(s)`);
    clearSelection();
  }, [selectedIds, clearSelection]);

  const handleBulkExport = useCallback(() => {
    toast.success(`${selectedIds.size} commande(s) exportée(s)`);
    clearSelection();
  }, [selectedIds, clearSelection]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const allPageSelected = orders.length > 0 && orders.every((o) => selectedIds.has(o.id));
  const somePageSelected = orders.some((o) => selectedIds.has(o.id));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-stone-900">
              Commandes
            </h1>
            <p className="text-sm text-stone-500">
              Suivi et gestion des commandes clients
            </p>
          </div>
          <ExportButton
            data={orders as unknown as Record<string, unknown>[]}
            columns={EXPORT_COLUMNS}
            filename="commandes-djayma"
          />
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <KPICard
            label="Total commandes"
            value={kpis?.total ?? 0}
            icon={<ShoppingCart className="h-4 w-4" />}
            format="number"
          />
          <KPICard
            label="En attente"
            value={kpis?.pending ?? 0}
            icon={<Clock className="h-4 w-4" />}
            format="number"
          />
          <KPICard
            label="En cours"
            value={kpis?.inProgress ?? 0}
            icon={<Truck className="h-4 w-4" />}
            format="number"
          />
          <KPICard
            label="Livrées"
            value={kpis?.delivered ?? 0}
            icon={<CheckCircle2 className="h-4 w-4" />}
            format="number"
          />
          <KPICard
            label="CA du jour"
            value={kpis?.todayRevenue ?? 0}
            icon={<Wallet className="h-4 w-4" />}
            format="currency"
          />
        </div>

        {/* Search + filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
          <div className="w-full max-w-sm">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); resetPage(); }}
              placeholder="Rechercher par référence, client..."
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as OrderStatus | ""); resetPage(); }}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les statuts</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {ORDER_STATUS_LABELS[s] ?? s}
              </option>
            ))}
          </select>
          <select
            value={paymentMethodFilter}
            onChange={(e) => { setPaymentMethodFilter(e.target.value); resetPage(); }}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Méthode paiement</option>
            {PAYMENT_METHOD_OPTIONS.map((m) => (
              <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m] ?? m}</option>
            ))}
          </select>
        </div>

        {/* Orders table */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          {/* Error state */}
          {isError && (
            <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
              <AlertCircle className="h-8 w-8 text-red-300" />
              <p className="text-sm font-medium text-stone-600">Impossible de charger les commandes</p>
              <p className="text-xs text-stone-400">Vérifiez votre connexion et réessayez.</p>
            </div>
          )}

          {/* Loading skeleton */}
          {isLoading && !data && (
            <div className="divide-y divide-stone-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <div className="h-4 w-4 rounded bg-stone-100" />
                  <div className="h-4 w-28 rounded bg-stone-100" />
                  <div className="hidden h-4 w-36 rounded bg-stone-100 sm:block" />
                  <div className="ml-auto h-4 w-20 rounded bg-stone-100" />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !isError && orders.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
              <ShoppingCart className="h-10 w-10 text-stone-200" />
              <p className="text-sm font-medium text-stone-500">Aucune commande trouvée</p>
              <p className="text-xs text-stone-400">Modifiez vos filtres pour afficher des résultats.</p>
            </div>
          )}

          {/* Table */}
          {!isError && orders.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="w-10 px-3 py-3">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = somePageSelected && !allPageSelected;
                        }}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Référence
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 sm:table-cell">
                      Client
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 md:table-cell">
                      Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Total
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 md:table-cell">
                      Paiement
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 sm:table-cell">
                      Région
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Statut
                    </th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {orders.map((order) => {
                    const isSelected = selectedIds.has(order.id);
                    return (
                      <tr
                        key={order.id}
                        className={`transition-colors ${
                          isSelected ? "bg-amber-50/50" : "hover:bg-stone-50"
                        }`}
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(order.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-stone-800">{order.reference}</span>
                        </td>
                        <td className="hidden px-4 py-3 sm:table-cell">
                          <div>
                            <p className="text-stone-800">{order.customer_name}</p>
                            <p className="text-xs text-stone-400">{order.customer_phone}</p>
                          </div>
                        </td>
                        <td className="hidden px-4 py-3 tabular-nums text-stone-500 md:table-cell">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-stone-800">
                          {formatFCFA(order.total_ttc)}
                        </td>
                        <td className="hidden px-4 py-3 text-stone-600 md:table-cell">
                          {PAYMENT_METHOD_LABELS[order.payment_method] ?? order.payment_method}
                        </td>
                        <td className="hidden px-4 py-3 text-stone-500 sm:table-cell">
                          {order.region?.name ?? "—"}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={order.status} variant="order" />
                        </td>
                        <td className="px-2 py-3">
                          <button
                            onClick={() => navigate(`/admin/orders/${order.id}`)}
                            className="rounded-md p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
                            aria-label="Voir détails"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalOrders > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-stone-100 px-4 py-3">
              <p className="text-xs text-stone-500">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalOrders)} sur {totalOrders} commandes
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-md p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  // Show pages around current page
                  const offset = Math.max(0, Math.min(page - 4, totalPages - 7));
                  return offset + i + 1;
                }).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`h-8 w-8 rounded-md text-xs font-medium transition-colors ${
                      p === page
                        ? "bg-amber-100 text-amber-800"
                        : "text-stone-600 hover:bg-stone-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-md p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk actions bar */}
        {selectedIds.size > 0 && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            actions={[
              { label: "Mettre à jour le statut", onClick: handleBulkStatusUpdate },
              { label: "Assigner livraison", onClick: handleBulkAssignDelivery },
              { label: "Exporter", onClick: handleBulkExport },
            ]}
            onClear={clearSelection}
          />
        )}
      </div>
    </AdminLayout>
  );
}
